import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hipobuy-spreadsheet.me.uk';

interface ArticleSection {
  heading: string;
  content: string[];
}

interface ArticleData {
  title: string;
  excerpt: string;
  publishedDate: string;
  author: string;
  readTime: string;
  category: string;
  sections: ArticleSection[];
  conclusion: string;
}

interface ArticleLayoutProps {
  article: ArticleData;
  slug: string;
}

export default function ArticleLayout({ article, slug }: ArticleLayoutProps) {
  const canonicalUrl = `${siteUrl}/${slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `${siteUrl}/#resources`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: canonicalUrl,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedDate,
    dateModified: article.publishedDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <article className="pt-24 md:pt-28 pb-16">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <nav className="flex items-center space-x-2 text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-600 transition-colors">
              Home
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-600">{article.category}</span>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-accent-600 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
              <span className="text-xs text-foreground-400">{article.publishedDate}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
              {article.excerpt}
            </p>

            <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-background-200/50">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-user-line text-accent-600"></i>
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-900">
                  {article.author}
                </div>
                <div className="text-xs text-foreground-400">
                  Published: {article.publishedDate}
                </div>
              </div>
            </div>
          </header>

          <div>
            {article.sections.map((section, sIdx) => (
              <section key={sIdx} className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <div className="bg-accent-50 rounded-2xl p-6 md:p-8 my-10 border border-accent-200/50">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                Key Takeaway
              </h3>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.conclusion}
              </p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                  Was this guide helpful?
                </h4>
                <p className="text-xs text-foreground-400">
                  Explore more resources below or return to the homepage.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Link
                  to="/"
                  className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-home-line"></i>
                  </span>
                  <span>Back to Home</span>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-semibold hover:border-foreground-400 transition-colors whitespace-nowrap"
                >
                  <span>Hipobuy Sheet</span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 p-6 bg-background-100 rounded-2xl border border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              Explore More Resources
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Complete Guide 2026', slug: 'hipobuy-spreadsheet-guide' },
                { label: 'Full Review & Analysis', slug: 'hipobuy-spreadsheet-review' },
                { label: 'Kakobuy vs Hipobuy', slug: 'hipobuy-vs-kakobuy-spreadsheet' },
                { label: 'Safety & Privacy Guide', slug: 'hipobuy-spreadsheet-safety' },
                { label: 'Beginner Tutorial', slug: 'hipobuy-spreadsheet-beginners' },
                { label: 'Expert Tips & Tricks', slug: 'hipobuy-spreadsheet-tips' },
              ].filter((r) => r.slug !== slug).map((resource) => (
                <Link
                  key={resource.slug}
                  to={`/${resource.slug}`}
                  className="p-3 rounded-lg bg-background-50 border border-background-200/50 hover:border-accent-300 transition-colors text-sm text-foreground-700 hover:text-accent-700"
                >
                  {resource.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}